import { useQuery } from '@tanstack/vue-query';
import { computed, type ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { getRecentResolvedTicket } from '@/common/userRequest';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';

const ticketIdEligibleForMark = (
  data: {
    ticket: { id: number | string } | null;
    ticketReviews?: { length: number };
  } | null
): number | string | null => {
  if (!data?.ticket || (data.ticketReviews?.length ?? 0) > 0) return null;
  return data.ticket.id;
};

/**
 * Fetches which ticket (if any) should show the “recently resolved” dot on the closed/resolved list.
 * TanStack Query cache + SWR-style revalidation (stale immediately, refetch on focus/reconnect).
 */
export const useRecentResolvedTicketIdQuery = (requesterId: ComputedRef<string | number>) => {
  const mockedFlowStore = useMockedFlowStore();
  const {
    isActive: isMockedFlowActive,
    hasRecentResolvedTicket,
    recentResolvedTicket: mockedRecentResolvedTicket,
  } = storeToRefs(mockedFlowStore);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: computed(() => ['conversations', 'recentResolvedTicketId', requesterId.value] as const),
    queryFn: async (): Promise<number | string | null> => {
      if (isMockedFlowActive.value) {
        if (!hasRecentResolvedTicket.value || !mockedRecentResolvedTicket.value) {
          return null;
        }
        return ticketIdEligibleForMark(mockedRecentResolvedTicket.value);
      }

      const id = requesterId.value;
      if (!id) return null;

      const fetched = await getRecentResolvedTicket(id);
      return ticketIdEligibleForMark(fetched);
    },
    enabled: computed(() => Boolean(requesterId.value) || isMockedFlowActive.value),
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const recentResolvedTicketId = computed(() => data.value ?? null);

  return {
    recentResolvedTicketId,
    isPending,
    isError,
    error,
    refetch,
  };
};
